import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../../shared/theming/material/material.module';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartType } from 'chart.js';
import {
  TimeSeriesData,
  DailyMetrics,
} from '../../../../shared/models/time-series.model';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { startOfWeek, endOfWeek } from 'date-fns';

interface MonthlyChartData {
  month: string;
  year: number;
  chartData: ChartConfiguration['data'];
}

interface WeeklyChartData {
  week: string[];
  chartData: ChartConfiguration['data'];
}

@Component({
  selector: 'app-time-series-chart',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    BaseChartDirective,
    FormsModule,
    MatButtonModule,
    MatButtonToggleModule,
  ],
  templateUrl: './time-series-chart.component.html',
  styleUrls: ['./time-series-chart.component.scss'],
})
export class TimeSeriesChartComponent implements OnChanges {
  @Input() data: TimeSeriesData | null = null;
  @Input() isLoading = false;

  public monthlyCharts: MonthlyChartData[] = [];
  public weeklyCharts: WeeklyChartData[] = [];

  viewMode: 'monthly' | 'weekly' = 'monthly';

  public chartOptions: ChartConfiguration['options'] = this.buildChartOptions();

  private buildChartOptions(): ChartConfiguration['options'] {
    return {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          display: true,
          title: {
            display: true,
            text: this.viewMode === 'monthly' ? 'Day' : 'Week',
          },
        },
        y: {
          display: true,
          title: {
            display: true,
            text: 'Total Interactions',
          },
          beginAtZero: true,
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          mode: 'index',
          intersect: false,
        },
      },
    };
  }

  public chartType: ChartType = 'bar';

  onViewChange() {
    this.chartOptions = this.buildChartOptions();
    this.updateChartData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data) {
      this.updateChartData();
    }
  }

  private updateChartData(): void {
    if (!this.data || !this.data.dailyMetrics) {
      return;
    }

    // Group metrics by month and week
    const groupedByMonth = this.groupByMonth(this.data.dailyMetrics);
    const groupedByWeek = this.groupByWeek(this.data.dailyMetrics);

    // Create a chart for each month
    this.monthlyCharts = Object.entries(groupedByMonth).map(
      ([key, metrics]) => {
        const [year, month] = key.split('-');
        const monthName = new Date(
          parseInt(year),
          parseInt(month) - 1
        ).toLocaleDateString('en-US', {
          month: 'long',
          year: 'numeric',
        });

        const labels = metrics.map((metric) => this.getDayOfMonth(metric.date));
        const interactions = metrics.map((metric) => metric.totalInteractions);

        return {
          month: monthName,
          year: parseInt(year),
          chartData: {
            labels: labels,
            datasets: [
              {
                label: 'Total Interactions',
                data: interactions,
                backgroundColor: '#3f51b5',
                borderColor: '#3f51b5',
                borderWidth: 1,
              },
            ],
          },
        };
      }
    );

    // Create a chart for weekly
    this.weeklyCharts = Object.entries(groupedByWeek).map(([]) => {
      const labels = Object.keys(groupedByWeek).map((key) => {
        const [start, end] = key.split('_to_');
        return `${this.formatDate(start)} to ${this.formatDate(end)}`;
      });
      const interactions = Object.values(groupedByWeek).map((metrics) =>
        metrics.reduce((sum, metric) => sum + metric.totalInteractions, 0)
      );
      return {
        week: labels,
        chartData: {
          labels: labels,
          datasets: [
            {
              label: 'Total Interactions',
              data: interactions,
              backgroundColor: '#3f51b5',
              borderColor: '#3f51b5',
              borderWidth: 1,
            },
          ],
        },
      };
    });
  }

  private groupByMonth(
    metrics: DailyMetrics[]
  ): Record<string, DailyMetrics[]> {
    return metrics.reduce(
      (acc, metric) => {
        const date = new Date(metric.date);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(metric);

        return acc;
      },
      {} as Record<string, DailyMetrics[]>
    );
  }

  private groupByWeek(metrics: DailyMetrics[]): Record<string, DailyMetrics[]> {
    return metrics.reduce(
      (acc, metric) => {
        const date = new Date(metric.date);
        const start = startOfWeek(date, { weekStartsOn: 1 });
        const end = endOfWeek(date, { weekStartsOn: 1 });

        const key = `${start.toISOString().slice(0, 10)}_to_${end.toISOString().slice(0, 10)}`;

        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(metric);

        return acc;
      },
      {} as Record<string, DailyMetrics[]>
    );
  }

  private getDayOfMonth(dateString: string): string {
    const date = new Date(dateString);
    return date.getDate().toString();
  }

  hasData(): boolean {
    return (
      this.data !== null &&
      this.data.dailyMetrics !== undefined &&
      this.data.dailyMetrics.length > 0
    );
  }

  private formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }
}
